import { useCallback, useEffect, useMemo, useState } from 'react';

import { DualCoordinateDepthRange } from './dual-coordinate-depth-range';
import { LengthRange } from './lengthRange.gen';

/**
 * An allocation between image (renderable/tray) and ruler (downhole) coordinate spaces.
 * A zero-width `rulerDepth` denotes a crop (no ruler distance is allocated to that image span).
 */
export type ImageToRulerLengthAllocation = DualCoordinateDepthRange;
export type ImageToRulerLengthAllocations = ImageToRulerLengthAllocation[];

export type DepthConversionFn = (depth: number) => number | undefined;

/**
 * Provides derived information from a set of allocations, such as the image/ruler range, and functions to convert between them.
 * @interface UseImageToRulerAllocationUtilitiesState
 * @property rulerDepth Based on the allocations, derive the ruler (downhole) length range
 * @property renderableDepth Based on the allocations, derive the image (tray) length range
 * @property getRulerDepthFromImageDepth Provided an input depth in image m (tray), return the depth in ruler m (downhole)
 * @property getImageDepthFromRulerDepth Provided an input depth in ruler m (downhole), return the depth in image m (tray)
 */
export interface UseImageToRulerAllocationUtilitiesState {
	rulerDepth: LengthRange | undefined;
	renderableDepth: LengthRange | undefined;
	getRulerDepthFromImageDepth: DepthConversionFn;
	getImageDepthFromRulerDepth: DepthConversionFn;
}

export const rulerIsUnallocated = (allocation: ImageToRulerLengthAllocation): boolean =>
	allocation.rulerDepth.depthFrom === allocation.rulerDepth.depthTo;

/**
 * ensure that all allocations are sequential, and no conflicts or missing allocations are in the collection
 * @param {ImageToRulerLengthAllocations} allocations the allocations to validate
 * @returns {ImageToRulerLengthAllocations | undefined} the validated allocations, or undefined if the input allocations could not be validated
 */
export const useValidatedImageToRulerLengthAllocations = (
	allocations: ImageToRulerLengthAllocations,
): ImageToRulerLengthAllocations | undefined => {
	const [validatedAllocations, setValidatedAllocations] = useState<ImageToRulerLengthAllocations>();
	// Allocations must be validated; no conflicting, or missing allocations
	useEffect(() => {
		const sorted = [...allocations].sort((a, b) => a.renderableDepth.depthFrom - b.renderableDepth.depthFrom);
		// Only a single allocation
		if (sorted.length == 1) {
			if (!sorted[0].rulerDepth) throw Error('Allocations must have at least one DH range');
			setValidatedAllocations(sorted);
		}
		for (let i = 1; i < sorted.length; i++) {
			if (sorted[i].renderableDepth.depthFrom < sorted[i - 1].renderableDepth.depthTo) {
				throw Error('Allocations must be continuous, with no gaps or overlaps');
			}
		}
		setValidatedAllocations(sorted);

		return (): void => setValidatedAllocations(undefined);
	}, [allocations]);
	return validatedAllocations;
};

/**
 * Provides derived state and methods from a set of allocations, such as the downhole range, and functions to convert between tray and downhole depths
 * @param allocations of the image, if undefined, assume not yet loaded
 */
export const useImageToRulerAllocationUtilities = (
	allocations: ImageToRulerLengthAllocations | undefined,
): UseImageToRulerAllocationUtilitiesState => {
	const allocationsChecksum = allocations
		?.map(
			(a) =>
				`${a.renderableDepth.depthFrom}:${a.renderableDepth.depthTo}::${a.rulerDepth.depthFrom}:${a.rulerDepth.depthTo}`,
		)
		.join(`|`);

	const imageFrom = useMemo(
		() => allocations?.find((a) => a.renderableDepth)?.renderableDepth.depthFrom,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allocationsChecksum],
	);
	const imageTo = useMemo(
		() =>
			(allocations ? [...allocations] : undefined)?.reverse().find((a) => a.renderableDepth)?.renderableDepth
				.depthTo,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allocationsChecksum],
	);

	const rulerFrom = useMemo(
		() => allocations?.find((a) => a.rulerDepth)?.rulerDepth.depthFrom,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allocationsChecksum],
	);
	const rulerTo = useMemo(
		() => (allocations ? [...allocations] : undefined)?.reverse().find((a) => a.rulerDepth)?.rulerDepth.depthTo,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allocationsChecksum],
	);

	const getRulerDepthFromImageDepth: DepthConversionFn = useCallback(
		(depth) => {
			if (!allocations) return undefined;
			if (imageFrom === undefined) return undefined;
			if (imageTo === undefined) return undefined;
			const clampDepth = Math.max(Math.min(depth, imageTo), imageFrom);
			const allocationIdx = allocations.findIndex(
				(a) => a.renderableDepth.depthFrom <= clampDepth && clampDepth <= a.renderableDepth.depthTo,
			);
			//Cannot find an allocated depth!
			if (allocationIdx < 0) return undefined;

			if (!rulerIsUnallocated(allocations[allocationIdx])) {
				const ratio =
					(clampDepth - allocations[allocationIdx].renderableDepth.depthFrom) /
					(allocations[allocationIdx].renderableDepth.depthTo -
						allocations[allocationIdx].renderableDepth.depthFrom);
				return (
					allocations[allocationIdx].rulerDepth.depthFrom +
					ratio *
						(allocations[allocationIdx].rulerDepth.depthTo -
							allocations[allocationIdx].rulerDepth.depthFrom)
				);
			}
			// Find first DH allocation after our current
			return allocations.slice(allocationIdx).find((a) => !!a.rulerDepth)?.rulerDepth.depthFrom;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allocationsChecksum, imageFrom, imageTo],
	);

	const getImageDepthFromRulerDepth: DepthConversionFn = useCallback(
		(depth) => {
			if (!allocations) return undefined;
			if (rulerFrom === undefined) return undefined;
			if (rulerTo === undefined) return undefined;
			const clampDepth = Math.max(Math.min(depth, rulerTo), rulerFrom);

			const allocationIdx = allocations.findIndex(
				(a) =>
					!rulerIsUnallocated(a) &&
					a.rulerDepth.depthFrom <= clampDepth &&
					clampDepth <= a.rulerDepth?.depthTo,
			);

			if (allocationIdx === -1) return undefined;

			const ratio =
				(clampDepth - allocations[allocationIdx].rulerDepth.depthFrom) /
				(allocations[allocationIdx].rulerDepth.depthTo - allocations[allocationIdx].rulerDepth.depthFrom);

			return (
				allocations[allocationIdx].renderableDepth.depthFrom +
				ratio *
					(allocations[allocationIdx].renderableDepth.depthTo -
						allocations[allocationIdx].renderableDepth.depthFrom)
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[allocationsChecksum, rulerFrom, rulerTo],
	);

	const rulerDepth = useMemo(
		() =>
			rulerFrom !== undefined && rulerTo !== undefined
				? {
						depthFrom: rulerFrom,
						depthTo: rulerTo,
					}
				: undefined,
		[rulerFrom, rulerTo],
	);

	const renderableDepth = useMemo(
		() =>
			imageFrom !== undefined && imageTo !== undefined
				? {
						depthFrom: imageFrom,
						depthTo: imageTo,
					}
				: undefined,
		[imageFrom, imageTo],
	);

	return {
		rulerDepth,
		renderableDepth,
		getRulerDepthFromImageDepth,
		getImageDepthFromRulerDepth,
	};
};
